// =============================================================================
// Copyright   : 2024 semantics GmbH
// Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 156393 $
// Purpose     : llm hooks
// =============================================================================
// ---Third-party imports
import React from 'react'
import now from 'performance-now'
import {Item, Message, List, Checkbox} from 'semantic-ui-react'

// ---Framework imports
import log from 'uix/logging'
import {callIfFunc, history, merge, dict, keys, values, isString, size, isNil} from 'ldash'
import {Actions} from 'uix/registries'

import {XMenu} from 'uix/components'
import {
  useServerEvents,
  useResettableState,
  usePersistent,
  useActions,
  useEffect,
  useMemo,
  useState,
  usePersistentShallow,
  useDebounce,
  useInterval,
  usePrevious,
  useRef,
} from 'uix/hooks'

import {formatDateTime} from 'uix/utils'

// ---Local imports
// ---Exports

const usePausesOnMarkdownLinks = ({doPause, duration, pauseOnMarkdownLink}) => {
  // Track links that are already processed
  let [processedLinks, setProcessedLinks] = useState({})

  let checkForLinks = text => {
    // Regular expression to match markdown links [label](href)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
    let match
    let newLinks = {}

    // Iterate over all matches in the text
    while ((match = linkRegex.exec(text)) !== null) {
      const label = match[1]
      const href = match[2]
      if (href.startsWith('http')) {
        continue
      }

      const linkKey = `${label}|${href}` // Unique key for each link
      // If the link hasn't been processed yet
      if (!processedLinks[linkKey]) {
        doPause(duration, {label, href})
        newLinks[linkKey] = true // Mark link as processed
      }
    }

    // Update the processedLinks state if there are new links
    if (size(newLinks)) {
      setProcessedLinks(prevProcessedLinks => ({
        ...prevProcessedLinks,
        ...newLinks,
      }))
    }
  }

  return pauseOnMarkdownLink ? checkForLinks : () => null
}

function getFirstNWords(text, N) {
  const regex = /(\S+\s*)/g
  let matches = []
  let match
  while ((match = regex.exec(text)) !== null && matches.length < N) {
    matches.push(match[0])
  }
  return matches.join('')
}

const usePauses = ({}) => {
  // Store pauses as an array of intervals + some metadata {start, end, target}
  let [pauses, setPauses] = useState([])
  // world time is currently in pause
  let [activePause, setActivePause] = useState(null)

  useInterval(
    () => {
      if (activePause && !isTsInPauses(now())) {
        setActivePause(null)
      }
    },
    activePause ? 100 : null
  )
  // trigger a pause from now for N secs
  let pause = (seconds, target) => {
    let ts = now()
    let p = {start: ts, end: ts + seconds * 1000, target}
    setPauses(prevPauses => [...prevPauses, p])
    setActivePause(p)
  }
  // modify the end of a active pause to now
  let unpause = () => {
    let ts = now()
    setPauses(prevPauses => prevPauses.map(p => (isTsInPause(ts, p) ? {...p, end: ts} : p)))
  }

  let isTsInPause = (ts, pause) => ts >= pause.start && ts < pause.end
  let isTsInPauses = ts => pauses.some(pause => isTsInPause(ts, pause))

  // Calculate total paused time until ts
  let totalPausedTime = ts =>
    pauses.reduce((accum, pause) => {
      if (ts >= pause.end) {
        // Pause has ended
        return accum + (pause.end - pause.start)
      } else if (ts >= pause.start) {
        // Pause is currently ongoing
        return accum + (ts - pause.start)
      } else {
        // Pause hasn't started yet
        return accum
      }
    }, 0)

  // get remaining time in active pause for ts
  let getActivePause = ts => pauses.filter(p => isTsInPause(ts, p))[0]
  let remainingPauseTime = ts => getActivePause(ts)?.end - ts ?? 0

  return {pause, unpause, activePause, isTsInPauses, totalPausedTime, remainingPauseTime}
}

export const useDelayedText = ({text, wordsPerSec = 15, enabled, pauseOnMarkdownLink}) => {
  /* emit incoming text delayed if enabled
   */

  let [delayedText, setDelayedText] = useResettableState('', [wordsPerSec])
  let is_done = delayedText == text

  let [startTs, setStartTs] = useState(null)
  let started = !isNil(startTs)
  let active = (enabled || (!is_done && started)) && wordsPerSec > 0

  let prev_text = usePrevious(text, '')
  let more_text = prev_text.length < text.length

  let pauses = usePauses({pauseOnMarkdownLink})

  let checkForLinks = usePausesOnMarkdownLinks({
    doPause: pauses.pause,
    duration: 5,
    pauseOnMarkdownLink,
  })

  useEffect(() => {
    if (enabled && !started && more_text) {
      setStartTs(now())
      setDelayedText('')
    }
  }, [started, enabled, more_text])

  useInterval(
    () => {
      if (startTs) {
        let ts = now()
        // Check if we're currently in a pause

        if (pauses.isTsInPauses(ts)) {
          // Do not update the text during a pause
          return
        }

        let duration_secs = (ts - startTs - pauses.totalPausedTime(ts)) / 1000
        let num_words = Math.ceil(duration_secs * wordsPerSec)
        let next_delayedText = getFirstNWords(text, num_words)

        if (next_delayedText != delayedText) {
          setDelayedText(next_delayedText)
          checkForLinks(next_delayedText)
        }
      }
    },
    active ? 50 : null
  )

  return {text: active ? delayedText : text, pauses}
}

const generateUUID = () => {
  let dt = new Date().getTime()
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (dt + Math.random() * 16) % 16 | 0
    dt = Math.floor(dt / 16)
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
  return uuid
}

const useHistory = ({messages, setMessages, thread_id, set_thread_id}) => {
  let [history, setHistory] = usePersistent('history', [])
  return useMemo(() => {
    let addToHistory = ({messages, thread_id}) => {
      let in_hist = history.filter(h => h.thread_id === thread_id)[0] ?? {}
      return messages.length > 0
        ? setHistory(
            [
              ...history.filter(h => h.thread_id !== thread_id),
              {...in_hist, messages, thread_id, dt: formatDateTime(new Date())},
            ].slice(-15)
          )
        : null
    }

    return {
      history,
      setHistory,
      addToHistory,
      clearHistory: () => setHistory(history.filter(h => h.pinned)),
      loadThreadFromHistory: thread_id => {
        let data = history.filter(h => h.thread_id == thread_id)[0]
        if (data) {
          let in_hist = history.filter(h => h.thread_id === thread_id)[0]
          if (!in_hist || in_hist.messages.length < messages.length) {
            addToHistory({messages, thread_id}) // preserve current (if new messages)
          }
          setMessages(data.messages)
          set_thread_id(data.thread_id)
        }
      },
      pinThreadInHistory: (thread_id, pinned) =>
        setHistory(history.map(h => (h.thread_id == thread_id ? {...h, pinned} : h))),
    }
  }, [history, setHistory, setMessages, set_thread_id])
}

const useStreamedPrompt = ({prompt}) => {
  let [thread_id, set_thread_id] = useState(useMemo(() => generateUUID(), []))
  let [messages, setMessages] = useState([])
  let {loading} = useChat({messages, setMessages, thread_id, set_thread_id})
  return {loading, response: ''}
}

export const usePersistentChat = props => {
  let [thread_id, set_thread_id] = useState(useMemo(() => generateUUID(), []))
  let [messages, setMessages] = usePersistent('current_chat', [])

  let historyctrl = useHistory({messages, setMessages, thread_id, set_thread_id})
  let onNewChat = () => {
    historyctrl.addToHistory({messages, thread_id})
  }
  let chat = useChat({...props, messages, setMessages, thread_id, set_thread_id, onNewChat})
  return {...chat, historyctrl}
}

export const useContextToggles = contextRules => {
  let knownContexts = keys(contextRules)
  let initial = dict(knownContexts.map(name => [name, false]))
  let [toggleState, setToggleState] = usePersistentShallow('contextToogleState', initial)
  let checkboxes = knownContexts.map(name => (
    <List.Item key={name}>
      <Checkbox
        label={contextRules[name].label || name}
        checked={toggleState[name]}
        onChange={() => setToggleState({[name]: !toggleState[name]})}
      />
    </List.Item>
  ))

  //~ log.x(toggleState)
  let menuItem = (
    <XMenu.PopupItem padded content="Context" keepOpen>
      <List>{checkboxes}</List>
    </XMenu.PopupItem>
  )

  return {menuItem: knownContexts.length == 0 ? null : menuItem, toggleState}
}

const useChat = ({
  messages,
  setMessages,
  thread_id,
  set_thread_id,
  context = [],
  model = '4m',
  temperature,
  onFunctionCall,
  onNewChat,
  contextRules,
  systemMessages,
  setRefocusOnPromptInput, // Ensure this method is part of the props passed to useChat
}) => {
  let events = useServerEvents('EStreamAiResponse')

  let [stream_id, set_stream_id] = useState(null)
  let [stream_info, set_stream_info] = useState({})
  let loading = stream_id !== null
  let [collectedStream, setCollectedStream] = useResettableState([], [stream_id])

  let reset = () => {
    callIfFunc(onNewChat)
    // run reset functions for outer context rules
    values(contextRules)
      .filter(rule => rule.onNewChat)
      .forEach(rule => rule.onNewChat())

    setMessages([])

    set_stream_id(null)
    set_stream_info(null)
    callIfFunc(onFunctionCall, null)
    set_thread_id(generateUUID())
  }
  let callApi = useActions('callApi')
  let openStream = data => {
    // start streaming
    let {stream_id, ...stream_info} = data
    set_stream_id(stream_id)
    set_stream_info(stream_info)
  }

  let runMessages = msgs => {
    setMessages(msgs)
    // run LLM
    // remove .meta prop
    msgs = msgs.map(({role, content}) => ({role, content}))
    let context_meta = merge({}, ...context.map(name => contextRules[name].buildData()))
    let sys_context = {}
    if (size(context)) {
      // trigger server side context enrichment
      // transport add client side context data in context_meta for further embedding
      sys_context = {context, context_meta: JSON.stringify(context_meta)}
    }

    if (size(systemMessages)) {
      // client side system message config
      sys_context = {
        ...sys_context,
        system_messages: isString(systemMessages) ? [systemMessages] : systemMessages,
      }
    }

    callApi(
      'openai/chat',
      {
        model,
        temperature,
        thread_id,
        messages: JSON.stringify(msgs),
        ...sys_context,
      },
      openStream
    )
  }

  // add a user message
  let newPrompt = prompt => {
    if (prompt) {
      let msgs = [...messages, {role: 'user', content: prompt}]
      runMessages(msgs)
    }
  }

  // given a message index - retry from there (kill later history)
  let retryFromUserMessage = index => {
    runMessages(messages.slice(0, index + 1))
  }

  // given a message index - retry from there (kill later history)
  let editMessage = (index, content, execute) => {
    let new_msgs = messages.map((msg, i) => (i == index ? {...msg, content} : msg))
    if (execute) {
      runMessages(new_msgs.slice(0, index + 1))
    } else {
      setMessages(new_msgs)
    }
  }

  let rate_limits = useMemo(() => {
    return events.filter(e => e.meta?.rate_limits).at(-1)?.meta.rate_limits || {}
  }, [events])

  // copy streamed events to collected
  useEffect(() => {
    let evts = events.filter(e => e.stream_id == stream_id)

    if (collectedStream.length) {
      let max_i = Math.max(...collectedStream.map(e => e.i))
      evts = evts.filter(e => e.i > max_i)
    }
    setCollectedStream([...collectedStream, ...evts])
  }, [events, stream_id])

  let buildAssistantMessage = (collected, event) => {
    let content = collected
      .filter(e => e.text)
      .map(e => e.text)
      .join('')
    return {role: 'assistant', content, meta: event?.meta, finish_reason: event?.finish_reason}
  }

  // check for stream-finish
  useEffect(() => {
    let finished_event = collectedStream.find(e => e.finish_reason)
    if (finished_event) {
      // build message from stream
      setMessages([...messages, buildAssistantMessage(collectedStream, finished_event)])
      if (finished_event.meta?.function_calls) {
        callIfFunc(onFunctionCall, finished_event.meta.function_calls[0])
      }

      // Increment refocusOnPromptInput when streaming is done
      callIfFunc(setRefocusOnPromptInput, prev => prev + 1)

      // disconnect from stream
      set_stream_id(null)
    }
  }, [collectedStream])

  // combine old messages and current partial streamed messages
  let currentMessages = useMemo(() => {
    return loading
      ? [...messages, buildAssistantMessage(collectedStream, {meta: {streaming: true}})]
      : messages
  }, [messages, collectedStream])

  // debug support
  let debug_data = useMemo(() => ({...stream_info, messages}), [messages, stream_info])

  return {
    messages: currentMessages,
    reset,
    newPrompt,
    loading,
    rate_limits,
    debug_data,
    // interact with messages
    interact: {retryFromUserMessage, editMessage},
  }
}
